import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Spinner, Crown, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeader, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid payment session');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: getAuthHeader()
        });

        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setMessage('Payment successful! Welcome to the Inner Circle!');
          // Refresh user data to get updated role
          if (refreshUser) {
            await refreshUser();
          }
        } else if (data.status === 'expired') {
          setStatus('failed');
          setMessage('Payment session expired. Please try again.');
        } else if (attempts < maxAttempts) {
          // Still pending, poll again
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('failed');
          setMessage('Payment verification timed out. If you were charged, please contact support.');
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('error');
          setMessage('Failed to verify payment. Please contact support if you were charged.');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts, getAuthHeader, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center"
      >
        {/* Status Icon */}
        <div className="mb-8">
          {status === 'checking' && (
            <div className="w-24 h-24 mx-auto bg-[#c9a227]/10 rounded-full flex items-center justify-center">
              <Spinner size={48} className="text-[#c9a227] animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-24 h-24 mx-auto bg-green-500/10 rounded-full flex items-center justify-center"
            >
              <CheckCircle size={48} weight="fill" className="text-green-500" />
            </motion.div>
          )}
          {(status === 'failed' || status === 'error') && (
            <div className="w-24 h-24 mx-auto bg-red-500/10 rounded-full flex items-center justify-center">
              <XCircle size={48} weight="fill" className="text-red-500" />
            </div>
          )}
        </div>

        {/* Status Message */}
        <h1 className="text-2xl md:text-3xl font-black text-white mb-4">
          {status === 'checking' && 'Verifying Payment...'}
          {status === 'success' && 'Welcome to the Inner Circle!'}
          {status === 'failed' && 'Payment Failed'}
          {status === 'error' && 'Verification Error'}
        </h1>

        <p className="text-white/60 mb-8">{message}</p>

        {/* Success Content */}
        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <div className="p-6 bg-[#111111] border border-[#c9a227]/30">
              <Crown size={32} weight="fill" className="text-[#c9a227] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Your Inner Circle Benefits</h3>
              <ul className="text-left text-white/70 text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-[#c9a227]" />
                  Premium Video Content
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-[#c9a227]" />
                  Inner Circle Community Access
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-[#c9a227]" />
                  Advanced Business Training
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-[#c9a227]" />
                  Weekly Accountability Sessions
                </li>
              </ul>
            </div>

            <Button
              onClick={() => navigate('/community')}
              data-testid="enter-inner-circle-btn"
              className="w-full rounded-none bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold py-4 flex items-center justify-center gap-2"
            >
              Enter the Inner Circle
              <ArrowRight size={20} />
            </Button>
          </motion.div>
        )}

        {/* Failed/Error Content */}
        {(status === 'failed' || status === 'error') && (
          <div className="space-y-4">
            <Button
              onClick={() => navigate('/inner-circle-sales')}
              className="w-full rounded-none bg-[#c9a227] hover:bg-[#b8922a] text-black font-bold"
            >
              Try Again
            </Button>
            <Link
              to="/community"
              className="block text-white/60 hover:text-white text-sm transition-colors"
            >
              Return to Community
            </Link>
          </div>
        )}

        {/* Checking Content */}
        {status === 'checking' && (
          <p className="text-white/40 text-sm">
            Attempt {attempts + 1} of {maxAttempts}...
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
